import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useRef, useState } from "react"
import type { Resolver } from "react-hook-form"
import { FaPlus } from "react-icons/fa"

import { useCreateEnrollment } from "@/hooks/api/useEnrollments"
import { usePermissions } from "@/hooks/usePermissions"
import { enrollmentCreateSchema } from "@/types/enrollment"
import type { AddEnrollmentProps, EnrollmentFormData } from "@/types/enrollment"
import { Button } from "@chakra-ui/react"

import EnrollmentFormDialog from "./EnrollmentFormDialog"

const AddEnrollment = memo(
  ({ onSuccess, onCancel }: AddEnrollmentProps = {}) => {
    const [isOpen, setIsOpen] = useState(false)
    const { hasPermission } = usePermissions()
    const createEnrollmentMutation = useCreateEnrollment()
    const addButtonRef = useRef<HTMLButtonElement | null>(null)

    if (!hasPermission("enrollments:create")) {
      return null
    }

    const defaultValues = useMemo<EnrollmentFormData>(
      () => ({
        student_id: "",
        class_template_id: "",
        session_id: "",
        notes: "",
      }),
      [],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: EnrollmentFormData) => {
      // The API accepts invoice_data for automatic invoice creation.
      // We pass a default invoice_data so the backend creates an invoice.
      // The actual amount_due will be calculated by the backend from the class template price.
      await createEnrollmentMutation.mutateAsync({
        student_id: data.student_id || "",
        class_template_id: data.class_template_id || "",
        session_id: data.session_id || "",
        status: data.status,
        notes: data.notes,
        invoice_data: {
          amount_due: 0, // Backend will calculate from class template
          amount_paid: 0,
          payment_status: "pending",
          payment_method: null,
          payment_date: null,
          invoice_number: null,
          notes: null,
        },
      })
      onSuccess?.()
    }

    return (
      <>
        <Button
          ref={addButtonRef}
          onClick={() => setIsOpen(true)}
          colorPalette="blue"
          size="sm"
          aria-label="Add new enrollment"
        >
          <FaPlus fontSize="14px" aria-hidden="true" />
          Add Enrollment
        </Button>

        <EnrollmentFormDialog
          isOpen={isOpen}
          title="Add New Enrollment"
          submitLabel="Create Enrollment"
          resolver={
            zodResolver(enrollmentCreateSchema) as Resolver<EnrollmentFormData>
          }
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={createEnrollmentMutation.isPending}
          finalFocusRef={addButtonRef}
          showSkipLink
          loadingText="Creating..."
        />
      </>
    )
  },
)

AddEnrollment.displayName = "AddEnrollment"

export default AddEnrollment
export type { AddEnrollmentProps }
